import {
    StoreLocatorQueryApi,
    StoreLocatorStoreFragmentApi,
    useStoreLocatorQueryApi,
    useStoreTerritorialUnitsQueryApi,
} from 'graphql/generated';
import { normalizeSearchValue } from 'helpers/parsing/normalizeSearchValue';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useMemo } from 'react';
import { StoreLocatorStoreType, StoreLocatorStoreTypeGroups, StoreServiceIconsType } from 'types/store';

const mapStoreLocatorStore = (store: StoreLocatorStoreFragmentApi): StoreLocatorStoreType => ({
    slug: store.slug,
    uuid: store.uuid,
    name: store.name,
    locationLatitude: store.locationLatitude === null ? null : Number.parseFloat(store.locationLatitude),
    locationLongitude: store.locationLongitude === null ? null : Number.parseFloat(store.locationLongitude),
    acceptGiftCoupon: store.acceptGiftCoupon,
    hasLoyaltyProgram: store.hasLoyaltyProgram,
    isPickupPoint: store.isPickupPoint,
    street: store.street ?? '',
    city: store.city ?? '',
    postcode: store.postcode ?? '',
    region: store.region,
    district: store.district,
    normalizedSearchText: normalizeSearchValue(
        [store.name, store.city, store.postcode, store.postcode?.replace(/\s/g, ''), store.region, store.district]
            .filter(Boolean)
            .join(' '),
    ),
});

const mapStoreLocatorGroups = (data: StoreLocatorQueryApi['stores']): StoreLocatorStoreTypeGroups => ({
    tescoma: data.tescoma?.map(mapStoreLocatorStore) ?? [],
    partner: data.partner?.map(mapStoreLocatorStore) ?? [],
});

export const useStoreLocatorStores = (): {
    stores: StoreLocatorStoreTypeGroups;
    serviceIcons: StoreServiceIconsType;
    isLoading: boolean;
} => {
    const [{ data, error, fetching }] = useStoreLocatorQueryApi();
    useQueryError(error);

    const stores = useMemo(
        () => (data === undefined ? { tescoma: [], partner: [] } : mapStoreLocatorGroups(data.stores)),
        [data],
    );

    return {
        stores,
        serviceIcons: {
            acceptGiftCouponIcon: data?.stores.acceptGiftCouponIcon ?? null,
            hasLoyaltyProgramIcon: data?.stores.hasLoyaltyProgramIcon ?? null,
            isPickupPointIcon: data?.stores.isPickupPointIcon ?? null,
        },
        isLoading: fetching,
    };
};

export type StoreTerritorialUnitType = {
    region: string;
    district: string | null;
};

export const useStoreTerritorialUnits = (
    enabled: boolean,
): { territorialUnits: StoreTerritorialUnitType[]; isLoading: boolean } => {
    const [{ data, error, fetching }] = useStoreTerritorialUnitsQueryApi({
        pause: !enabled,
        requestPolicy: 'cache-first',
    });
    useQueryError(error);

    return {
        territorialUnits: data?.storeTerritorialUnits ?? [],
        isLoading: fetching,
    };
};
